import config from '../../config';
import { useCallback, useState } from 'react';
import RefreshIcon from '@ordr-as/icons/RefreshIcon';
import { Button } from '@ordr-as/umami-react/button';
import { Dialog } from '@ordr-as/umami-react/dialog';
import { useQueryClient } from '@tanstack/react-query';
import { useCartStore } from '../../context/cart/CartStore';
import { isAdyenDevice } from '../../utils/isAdyenDevice';

interface DiagnosticsDialogProps {
  closeDialog: () => void;
}

const formatBytes = (bytes: number) => {
  if (bytes === 0) {
    return '0 B';
  }
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Number.parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
};

const getSize = (obj: unknown): number => {
  try {
    return new TextEncoder().encode(JSON.stringify(obj)).length;
  } catch {
    return 0;
  }
};

interface Diagnostics {
  queryCount: number;
  totalQuerySize: number;
  topQueries: Array<{ key: string; size: number }>;
  zustandStats: { cartStore: number };
  heap?: number;
  totalJsHeap: number;
  heapSizeLimit: number;
  domNodes?: number;
  deviceType: string;
}

const DiagnosticsDialog = ({ closeDialog }: DiagnosticsDialogProps) => {
  const queryClient = useQueryClient();
  const cartStoreState = useCartStore((state) => state.getCartSnapshot);

  const [diagnostics, setDiagnostics] = useState<Diagnostics>();

  const getDiagnostics = useCallback(() => {
    const queries = queryClient.getQueryCache().getAll();
    const queryStats = queries.map((q) => ({
      key: JSON.stringify(q.queryKey),
      size: getSize(q.state.data),
    }));

    const totalQuerySize = queryStats.reduce((sum, q) => sum + q.size, 0);
    const sortedQueries = [...queryStats].sort((a, b) => b.size - a.size).slice(0, 5);

    const zustandStats = {
      cartStore: getSize(cartStoreState()),
    };

    const perfWithMemory = performance as Performance & {
      memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number; totalJSHeapSize: number };
    };
    const heap = perfWithMemory.memory?.usedJSHeapSize ?? 0;
    const heapSizeLimit = perfWithMemory.memory?.jsHeapSizeLimit ?? 0;
    const totalJsHeap = perfWithMemory.memory?.totalJSHeapSize ?? 0;

    const domNodes = document.querySelectorAll('*').length;

    const deviceType = isAdyenDevice() ? 'S1F2' : '';

    const stats = {
      deviceType,
      domNodes,
      heap,
      heapSizeLimit,
      queryCount: queries.length,
      topQueries: sortedQueries,
      totalJsHeap,
      totalQuerySize,
      zustandStats,
    };

    setDiagnostics(stats);
  }, [queryClient, cartStoreState]);

  return (
    <Dialog
      onClose={closeDialog}
      actionLabel="Close"
      actionKind="primary"
      onAction={closeDialog}
      title={`Diagnostics ${diagnostics?.deviceType ?? ''}`}
    >
      <div>
        {diagnostics && (
          <>
            <strong>Queries</strong>
            <div>
              <ul>
                <li>Queries: {diagnostics.queryCount}</li>
                <li>Total size: {formatBytes(diagnostics.totalQuerySize)}</li>
              </ul>
            </div>
            <div>
              <strong>Cart</strong>
              <div className="mt-2">
                <ul>
                  <li>Cart store: {formatBytes(diagnostics.zustandStats.cartStore)}</li>
                </ul>
              </div>
            </div>
            <div>
              <strong>Heap</strong>
              {diagnostics.heap && (
                <div className="mt-2">
                  <ul>
                    <li>JS Heap Used {formatBytes(diagnostics.heap)}</li>
                    <li>Total JS Heap {formatBytes(diagnostics.totalJsHeap)}</li>
                    <li>JS Heap limit {formatBytes(diagnostics.heapSizeLimit)}</li>
                  </ul>
                </div>
              )}
            </div>
            <div>
              <strong>DOM</strong>
              <div className="mt-2">
                <ul>
                  <li>Nodes: {diagnostics.domNodes}</li>
                </ul>
              </div>
            </div>
          </>
        )}
      </div>
      <div className="mt-6">
        <Button onClick={getDiagnostics} kind="primary">
          {diagnostics ? 'Reload stats' : 'Load stats'}
        </Button>
      </div>
    </Dialog>
  );
};

interface AppReleaseVersionProps {
  isF1?: boolean;
}

const handleHardRefresh = () => {
  window.location.replace(window.location.href);
};

const AppReleaseVersion = ({ isF1 }: AppReleaseVersionProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  const closeDialog = () => {
    setDialogOpen(false);
  };

  const openDialog = () => {
    setDialogOpen(true);
  };

  return (
    <div className="flex flex-col text-right min-w-full justify-between p-2 pr-4">
      <span className="text-sm text-grey-dark flex items-center justify-end gap-1">
        <span className="mr-3 underline" onClick={openDialog}>
          Diagnosis
        </span>
        <i>v{isF1 ? `${config.release}-f1` : config.release}</i>

        <button onClick={handleHardRefresh} title="Refresh data">
          <RefreshIcon className="text-grey-dark hover:text-grey" fontSize={14} />
        </button>
      </span>
      {dialogOpen && <DiagnosticsDialog closeDialog={closeDialog} />}
    </div>
  );
};

export default AppReleaseVersion;
